import React, { useState, useEffect } from 'react';
import { useStores } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../utils/formatters';

const Stores = () => {
  const { user, isAdmin } = useAuth();
  const { 
    stores, 
    loading, 
    error, 
    setError, 
    createStore, 
    updateStore, 
    deleteStore,
    fetchStores 
  } = useStores();
  
  const [showModal, setShowModal] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    description: '',
    monthly_goal: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleOpenModal = (store = null) => {
    if (store) {
      setEditingStore(store);
      setFormData({
        name: store.name,
        location: store.location,
        description: store.description || '',
        monthly_goal: store.monthly_goal || '',
      });
    } else {
      setEditingStore(null);
      setFormData({
        name: '',
        location: '',
        description: '',
        monthly_goal: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingStore(null);
    setFormData({
      name: '',
      location: '',
      description: '',
      monthly_goal: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const storeData = {
        ...formData,
        monthly_goal: formData.monthly_goal ? parseFloat(formData.monthly_goal) : null,
      };

      if (editingStore) {
        await updateStore(editingStore.id, storeData);
      } else {
        await createStore(storeData);
      }

      handleCloseModal();
    } catch (err) {
      console.error('Error saving store:', err);
      setError(editingStore ? 'Error al actualizar la tienda' : 'Error al crear la tienda');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (storeId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta tienda?')) {
      return;
    }

    try {
      await deleteStore(storeId);
    } catch (err) {
      console.error('Error deleting store:', err);
      setError('Error al eliminar la tienda');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Cargando tiendas...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestión de Tiendas</h1>
          <p className="text-gray-600 mt-1">
            {isAdmin 
              ? `Administra la información de tus ${stores.length} tiendas - Control Total`
              : `Consulta información de las ${stores.length} tiendas - Solo Lectura`
            }
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Role Badge */}
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            isAdmin 
              ? 'bg-green-100 text-green-800' 
              : 'bg-blue-100 text-blue-800'
          }`}>
            {isAdmin ? 'Admin - Edición Completa' : 'Empleado - Solo Consulta'}
          </div>
          
          <button onClick={fetchStores} className="btn-outline">
            🔄 Actualizar
          </button>
          
          {/* Only admins can create new stores */}
          {isAdmin ? (
            <button onClick={() => handleOpenModal()} className="btn-primary">
              ➕ Nueva Tienda
            </button>
          ) : (
            <div className="text-xs text-gray-500 px-3 py-2 bg-gray-100 rounded-md">
              ⚠️ Solo admins pueden crear tiendas
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="text-sm underline ml-2">
            Cerrar
          </button>
        </div>
      )}

      {/* Stores Grid */}
      {stores.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🏪</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay tiendas registradas</h3>
          <p className="text-gray-600 mb-4">
            {isAdmin 
              ? "Comienza agregando tu primera tienda" 
              : "No hay tiendas disponibles para consultar"
            }
          </p>
          {isAdmin ? (
            <button onClick={() => handleOpenModal()} className="btn-primary">
              ➕ Crear Primera Tienda
            </button>
          ) : (
            <div className="text-sm text-gray-500 px-4 py-2 bg-gray-100 rounded-md">
              ⚠️ Solo los administradores pueden crear tiendas
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stores.map((store) => (
            <div key={store.id} className="card hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 font-bold text-lg">🏪</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{store.name}</h3>
                    <p className="text-sm text-gray-600">{store.location}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {isAdmin ? (
                    <>
                      <button
                        onClick={() => handleOpenModal(store)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                        title="Editar tienda"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(store.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                        title="Eliminar tienda"
                      >
                        🗑️
                      </button>
                    </>
                  ) : (
                    <div className="text-xs text-gray-400 px-2 py-1 bg-gray-50 rounded">
                      Solo lectura
                    </div>
                  )}
                </div>
              </div>

              {store.description && (
                <p className="text-gray-600 text-sm mb-4">{store.description}</p>
              )}

              <div className="space-y-2">
                {store.monthly_goal && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Meta Mensual:</span>
                    <span className="font-medium text-green-600">
                      {formatCurrency(store.monthly_goal)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Creada:</span>
                  <span className="text-sm text-gray-900">
                    {formatDate(store.created_at)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Estado:</span>
                  <span className={`badge ${store.is_active ? 'badge-success' : 'badge-error'}`}>
                    {store.is_active ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingStore ? 'Editar Tienda' : 'Nueva Tienda'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body space-y-4">
                <div className="form-group">
                  <label className="form-label">Nombre de la Tienda *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Ej: Tienda Centro"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Ubicación *</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Ej: Calle Mayor 123, Madrid"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Descripción</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="form-textarea"
                    rows="3"
                    placeholder="Descripción opcional de la tienda..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Meta de Ventas Mensual ($)</label>
                  <input
                    type="number"
                    name="monthly_goal"
                    value={formData.monthly_goal}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="10000"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-outline"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary"
                >
                  {submitting ? (
                    <span className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      Guardando...
                    </span>
                  ) : (
                    editingStore ? 'Actualizar' : 'Crear'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stores;